﻿import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { Loader2 } from 'lucide-react';
import { format, addMonths, addDays } from 'date-fns';
import type { ApiError } from '@pms/shared';
import { subscriptionService } from '@/services/subscription.service';
import { departmentsService } from '@/services/departments.service';
import { usersService } from '@/services/users.service';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

const NONE = '__none__';

interface FormValues {
  name: string;
  vendor_name: string;
  category_id: string;
  department_id: string;
  billing_cycle_id: string;
  custom_interval_days: string;
  assigned_to: string;
  amount: string;
  currency_id: string;
  status_id: string;
  start_date: string;
  end_date: string;
  reminder_days: string;
  auto_renewal: boolean;
  notes: string;
}

function computeNextDue(startDate: string, cycleCode: string, intervalMonths: number | null, intervalDays: number | null, customDays: string): string | null {
  if (!startDate || !cycleCode) return null;
  const base = new Date(startDate);
  if (cycleCode === 'one_time') return null;
  if (cycleCode === 'custom') {
    const d = parseInt(customDays, 10);
    if (!d) return null;
    return format(addDays(base, d), 'dd MMM yyyy');
  }
  if (intervalMonths) return format(addMonths(base, intervalMonths), 'dd MMM yyyy');
  if (intervalDays) return format(addDays(base, intervalDays), 'dd MMM yyyy');
  return null;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export function CreateSubscriptionModal({ open, onClose }: Props) {
  const queryClient = useQueryClient();

  const { data: categories = [] } = useQuery({ queryKey: ['subscriptions', 'categories'], queryFn: () => subscriptionService.listCategories() });
  const { data: currencies = [] } = useQuery({ queryKey: ['subscriptions', 'currencies'], queryFn: () => subscriptionService.listCurrencies() });
  const { data: statuses = [] } = useQuery({ queryKey: ['subscriptions', 'statuses'], queryFn: () => subscriptionService.listStatuses() });
  const { data: billingCycles = [] } = useQuery({ queryKey: ['subscriptions', 'billing-cycles'], queryFn: () => subscriptionService.listBillingCycles() });
  const { data: deptsData } = useQuery({ queryKey: ['departments'], queryFn: () => departmentsService.list({ limit: 100 }) });
  const { data: usersData } = useQuery({ queryKey: ['users', 'basic'], queryFn: () => usersService.list({ limit: 100 }) });

  const defaultCurrencyId = currencies.find((c) => c.is_default)?.id ?? '';
  const defaultStatusId = statuses.find((s) => s.is_default)?.id ?? '';

  const { register, handleSubmit, reset, watch, setValue, control, formState: { errors, isSubmitting } } = useForm<FormValues>({
    defaultValues: {
      name: '', vendor_name: '', category_id: NONE, department_id: NONE,
      billing_cycle_id: '', custom_interval_days: '', assigned_to: NONE,
      amount: '', currency_id: '', status_id: '',
      start_date: format(new Date(), 'yyyy-MM-dd'),
      end_date: '', reminder_days: '7', auto_renewal: false, notes: '',
    },
  });

  useEffect(() => {
    if (open && (defaultCurrencyId || defaultStatusId)) {
      if (defaultCurrencyId) setValue('currency_id', defaultCurrencyId);
      if (defaultStatusId) setValue('status_id', defaultStatusId);
    }
  }, [open, defaultCurrencyId, defaultStatusId, setValue]);

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  const watchBillingCycleId = watch('billing_cycle_id');
  const watchStartDate = watch('start_date');
  const watchCustomDays = watch('custom_interval_days');
  const selectedCycle = billingCycles.find((c) => c.id === watchBillingCycleId);
  const nextDuePreview = selectedCycle
    ? computeNextDue(watchStartDate, selectedCycle.code, selectedCycle.interval_months, selectedCycle.interval_days, watchCustomDays)
    : null;

  const mutation = useMutation({
    mutationFn: (v: FormValues) =>
      subscriptionService.create({
        name: v.name.trim(),
        vendor_name: v.vendor_name.trim() || undefined,
        category_id: v.category_id === NONE ? undefined : v.category_id || undefined,
        department_id: v.department_id === NONE ? undefined : v.department_id || undefined,
        billing_cycle_id: v.billing_cycle_id,
        custom_interval_days: v.custom_interval_days ? parseInt(v.custom_interval_days, 10) : undefined,
        assigned_to: v.assigned_to === NONE ? undefined : v.assigned_to || undefined,
        amount: parseFloat(v.amount),
        currency_id: v.currency_id,
        status_id: v.status_id,
        start_date: v.start_date,
        end_date: v.end_date || undefined,
        reminder_days: parseInt(v.reminder_days, 10),
        auto_renewal: v.auto_renewal,
        notes: v.notes.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success('Subscription created');
      void queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      onClose();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-full max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>New Subscription</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Name */}
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="sub-name">Subscription Name *</Label>
              <Input id="sub-name" {...register('name', { required: 'Name is required' })} placeholder="e.g. GitHub Teams" />
              {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
            </div>

            {/* Vendor */}
            <div className="space-y-1.5">
              <Label htmlFor="sub-vendor">Vendor / Provider</Label>
              <Input id="sub-vendor" {...register('vendor_name')} placeholder="e.g. GitHub Inc." />
            </div>

            {/* Category */}
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Controller
                control={control}
                name="category_id"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>No category</SelectItem>
                      {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {/* Department */}
            <div className="space-y-1.5">
              <Label>Department</Label>
              <Controller
                control={control}
                name="department_id"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger><SelectValue placeholder="Select department" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>No department</SelectItem>
                      {(deptsData?.data ?? []).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {/* Billing Cycle */}
            <div className="space-y-1.5">
              <Label>Billing Cycle *</Label>
              <Controller
                control={control}
                name="billing_cycle_id"
                rules={{ required: 'Billing cycle is required' }}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger><SelectValue placeholder="Select cycle" /></SelectTrigger>
                    <SelectContent>
                      {billingCycles.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.billing_cycle_id && <p className="text-xs text-destructive">{errors.billing_cycle_id.message}</p>}
            </div>

            {/* Custom interval days — only if custom cycle selected */}
            {selectedCycle?.code === 'custom' && (
              <div className="space-y-1.5">
                <Label htmlFor="sub-custom-days">Interval (days) *</Label>
                <Input id="sub-custom-days" type="number" min={1} {...register('custom_interval_days', { required: 'Required for custom cycle' })} placeholder="e.g. 45" />
                {errors.custom_interval_days && <p className="text-xs text-destructive">{errors.custom_interval_days.message}</p>}
              </div>
            )}

            {/* Assigned To */}
            <div className="space-y-1.5">
              <Label>Assigned To</Label>
              <Controller
                control={control}
                name="assigned_to"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger><SelectValue placeholder="Select user" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Unassigned</SelectItem>
                      {(usersData?.data ?? []).map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {/* Amount */}
            <div className="space-y-1.5">
              <Label htmlFor="sub-amount">Amount *</Label>
              <Input
                id="sub-amount"
                type="number"
                step="0.01"
                min={0.01}
                {...register('amount', { required: 'Amount is required', validate: (v) => parseFloat(v) > 0 || 'Must be positive' })}
                placeholder="0.00"
              />
              {errors.amount && <p className="text-xs text-destructive">{errors.amount.message}</p>}
            </div>

            {/* Currency */}
            <div className="space-y-1.5">
              <Label>Currency *</Label>
              <Controller
                control={control}
                name="currency_id"
                rules={{ required: 'Currency is required' }}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger><SelectValue placeholder="Select currency" /></SelectTrigger>
                    <SelectContent>
                      {currencies.map((c) => <SelectItem key={c.id} value={c.id}>{c.symbol} {c.code} — {c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.currency_id && <p className="text-xs text-destructive">{errors.currency_id.message}</p>}
            </div>

            {/* Start Date */}
            <div className="space-y-1.5">
              <Label htmlFor="sub-start">Start Date *</Label>
              <Input id="sub-start" type="date" {...register('start_date', { required: 'Start date is required' })} />
              {errors.start_date && <p className="text-xs text-destructive">{errors.start_date.message}</p>}
            </div>

            {/* End Date */}
            <div className="space-y-1.5">
              <Label htmlFor="sub-end">End Date <span className="text-muted-foreground text-xs">(optional)</span></Label>
              <Input id="sub-end" type="date" {...register('end_date')} />
            </div>

            {/* Next due preview */}
            {nextDuePreview && (
              <div className="sm:col-span-2 rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm">
                <span className="text-muted-foreground">Computed next due date: </span>
                <span className="font-medium text-emerald-700">{nextDuePreview}</span>
              </div>
            )}

            {/* Status */}
            <div className="space-y-1.5">
              <Label>Status *</Label>
              <Controller
                control={control}
                name="status_id"
                rules={{ required: 'Status is required' }}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                    <SelectContent>
                      {statuses.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          <span className="flex items-center gap-2">
                            <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                            {s.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.status_id && <p className="text-xs text-destructive">{errors.status_id.message}</p>}
            </div>

            {/* Reminder Days */}
            <div className="space-y-1.5">
              <Label htmlFor="sub-reminder">Reminder Days</Label>
              <Input id="sub-reminder" type="number" min={0} {...register('reminder_days')} placeholder="7" />
            </div>

            {/* Auto Renewal */}
            <div className="flex items-center gap-2 pt-1">
              <Checkbox
                id="sub-auto"
                checked={watch('auto_renewal')}
                onChange={(e) => setValue('auto_renewal', e.target.checked)}
              />
              <Label htmlFor="sub-auto" className="cursor-pointer font-normal">Enable auto-renewal</Label>
            </div>

            {/* Notes */}
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="sub-notes">Notes</Label>
              <Textarea id="sub-notes" {...register('notes')} placeholder="Additional notes…" rows={2} />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={mutation.isPending || isSubmitting}>
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Create Subscription
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
